import {
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MoonOutlined,
  SunOutlined,
  UserOutlined,
} from '@ant-design/icons';

import type { MenuProps } from 'antd';
import {
  Button,
  Divider,
  Flex,
  Layout,
  Menu,
  message,
  Popover,
  Space,
  Tabs,
  Typography,
  theme as themeAnt,
} from 'antd';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import type { MenuInfo } from 'rc-menu/lib/interface';
import type React from 'react';
import { type PropsWithChildren, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { logout } from '@/auth/services/logout-service';
import { useAuthStore } from '@/auth/store/auth.store';
import type { MenuResponse } from '@/domain/menu.ts';
import { buildApiUrl } from '@/global.ts';
import MenuSkeleton from '@/shared/Components/LayoutTemplate/Menu.skeleton.tsx';
import { useFetch } from '@/shared/hook/useFetch';
import { usePost } from '@/shared/hook/usePost';
import { rawLogoff } from '@/shared/hook/usePostLogOff';
import { Context } from '@/store/Context.tsx';
import { ConfigurationContext } from '@/store/configuration/Context.tsx';
import type { UserProfile } from '@/utilities/permission-manager/permission-manager.ts';
import { TabsManager } from '@/utilities/tabs-manager/tabs-manager.tsx';
import { QuotaHeartbeatOrchestrator } from '../QuotaHeartbeatOrchestrator/index.tsx';
import { filterTabPageMap, getTransformToMenu } from './menu.tsx';

const { Sider } = Layout;

interface LevelKeysProps {
  key?: string;
  children?: LevelKeysProps[];
}

const getLevelKeys = (items1: LevelKeysProps[]) => {
  const key: Record<string, number> = {};
  const func = (items2: LevelKeysProps[], level = 1) => {
    items2.forEach((item) => {
      if (item.key) {
        key[item.key] = level;
      }
      if (item.children) {
        func(item.children, level + 1);
      }
    });
  };
  func(items1);
  return key;
};

const LayoutTemplate: React.FC<PropsWithChildren> = () => {
  const [intervalSession, setIntervalSession] = useState<ReturnType<typeof setInterval> | null>(null);
  const [stateOpenKeys, setStateOpenKeys] = useState(['2', '23']);
  const [isCollapsed, setisCollapsed] = useState(localStorage.getItem('collapsed') === 'collapsed');
  const { setIsDark, setAppError, isDark, error, tabs, setTabs, setTabsPageMap, activeTab, setActiveTab } =
    useContext(Context);
  // const [visibleActiveTab, setVisibleActiveTab] = useState(activeTab);

  const { setConfiguration } = useContext(ConfigurationContext);

  const breakpoint = useBreakpoint();
  const levelKeysRef = useRef<Record<string, number>>({});
  const fetchProfile = useFetch<UserProfile>(buildApiUrl('/Seguridad/profile'), {}, { disableAutoFetch: true });
  const fetchMenu = usePost<{ nombreUsuario: string }, MenuResponse[]>(buildApiUrl('/Seguridad/lista-menu-usuario'));

  // useEffect(() => {
  //   const frameId = window.requestAnimationFrame(() => {
  //     setVisibleActiveTab(activeTab);
  //   });

  //   return () => window.cancelAnimationFrame(frameId);
  // }, [activeTab]);

  useEffect(() => {
    if (error?.isSessionExpired) {
      setAppError(error);
    }
  }, [error]);

  const {
    token: { colorBgContainer, colorFillQuaternary },
  } = themeAnt.useToken();

  const tabPageMap = useMemo(() => {
    return filterTabPageMap(fetchMenu.data || []);
  }, [fetchMenu.data]);

  const removeIntervalSession = () => {
    if (intervalSession) {
      clearInterval(intervalSession);
      setIntervalSession(null);
    }
  };

  const isExecutingRef = useRef(false);

  const validateSession = async () => {
    if (isExecutingRef.current) return;
    isExecutingRef.current = true;
    try {
      const response = await fetch(buildApiUrl('/Cuenta/registra-historial'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.status === 401) {
        removeIntervalSession();
        await rawLogoff();
        return;
      }

      if (response.status === 429) {
        const res = await response.json().catch(() => null);
        message.error(res?.message || 'Error al validar sesión');
        setTimeout(() => {
          rawLogoff();
        }, 1000);
        return;
      }

      const result = await response.json().catch(() => null);
      if (response.status === 401 || result?.resultado === false) {
        // Solo cerramos sesión si expresamente nos rechaza por seguridad
        await rawLogoff();
      }
    } catch {
      console.warn('Network error or server unavailable during validateSession. Will retry later.');
      // No cerramos sesión por errores temporales de red
    } finally {
      isExecutingRef.current = false;
    }
  };

  useEffect(() => {
    if (tabPageMap) {
      setTabsPageMap(tabPageMap);
    }
  }, [tabPageMap]);

  useEffect(() => {
    const init = async () => {
      try {
        // Delay para asegurar que las cookies se procesen correctamente tras el login
        await new Promise((resolve) => setTimeout(resolve, 500));

        await validateSession();
        await fetchProfile.fetchData();
        fetchMenu.postData({
          nombreUsuario: localStorage.getItem('username') ?? '',
        });
      } catch (error) {
        console.error(error);
      }
    };

    init();
  }, []);

  useEffect(() => {
    if (fetchProfile.data) {
      // el saludo del chat de video (VideoRagChat) usa el mismo nombre que este header, sin otra llamada
      useAuthStore.getState().setFirstName(fetchProfile.data.firstName?.trim() || null);
      setConfiguration();
      validateSession();
      if (!intervalSession) {
        const intervalId = setInterval(validateSession, 1000 * 60 * 15);
        setIntervalSession(intervalId);
      }
    }
  }, [fetchProfile.data]);

  useEffect(() => {
    if (fetchMenu.data) {
      levelKeysRef.current = getLevelKeys(fetchMenu.data as LevelKeysProps[]);
    }
  }, [fetchMenu.data]);

  const onClickMenu = (info: MenuInfo) => {
    const tabsManager = new TabsManager(tabs);
    const path = info.key;
    const isFound = Boolean(tabPageMap[path]);

    if (!isFound) {
      console.warn('Path not found or not registered');
      return;
    }

    tabsManager.addTabByTabPage(tabPageMap[path]);

    setTabs([...tabsManager.getTabs()]);
    setActiveTab(path);
  };

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  const changeTheme = async () => {
    setIsDark(!isDark);
  };

  const onCloseSession = async () => {
    await logout();
  };

  const onRemoveTab = (key: string) => {
    const removeActiveTab = activeTab === key;
    const tabsManager = new TabsManager(tabs);
    const findIndex = tabsManager.findIndex(key);
    tabsManager.removeTab(key);
    setTabs(tabsManager.getTabs());

    if (removeActiveTab) {
      setActiveTab(tabs[findIndex - 1]?.key || '');
    }
  };

  const onTabClick = (key: string) => {
    setActiveTab(key as string);
    console.log({ key });
  };

  const shouldCollapsed = isCollapsed || !breakpoint.xl;

  const onOpenChange: MenuProps['onOpenChange'] = (openKeys) => {
    const currentOpenKey = openKeys.find((key) => stateOpenKeys.indexOf(key) === -1);
    if (currentOpenKey !== undefined) {
      const repeatIndex = openKeys
        .filter((key) => key !== currentOpenKey)
        .findIndex((key) => levelKeysRef.current[key] === levelKeysRef.current[currentOpenKey]);

      setStateOpenKeys(
        openKeys
          .filter((_, index) => index !== repeatIndex)
          .filter((key) => levelKeysRef.current[key] <= levelKeysRef.current[currentOpenKey])
      );
    } else {
      setStateOpenKeys(openKeys);
    }
  };

  return (
    <>
      <QuotaHeartbeatOrchestrator />

      <Layout
        style={{
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        {/* SIDEBAR */}
        <Sider
          trigger={null}
          collapsible
          collapsed={shouldCollapsed}
          width={250}
          theme="light"
          style={{
            backgroundColor: colorFillQuaternary,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <Flex
            vertical
            style={{
              height: '100%',
            }}
          >
            <Flex justify="center" style={{ padding: 12 }}>
              <Flex
                flex="1"
                align="center"
                justify="center"
                style={{
                  padding: 2,
                  borderRadius: 10,
                }}
              >
                <img
                  src={shouldCollapsed ? '/assets/ipbr_isotipo.png' : '/assets/logo_black.png'}
                  alt="Logo Broadcast"
                  style={{ height: 40 }}
                />
              </Flex>
            </Flex>

            <MenuSkeleton isLoading={fetchMenu.loading} isCollapsed={shouldCollapsed}>
              <Menu
                mode="inline"
                onClick={onClickMenu}
                openKeys={stateOpenKeys}
                onOpenChange={onOpenChange}
                defaultSelectedKeys={['Identificaciones']}
                items={getTransformToMenu(fetchMenu.data || [])}
              />
            </MenuSkeleton>
          </Flex>
        </Sider>

        {/* PANEL DERECHO */}
        <Layout
          style={{
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* TABS FIJAS */}
          <div
            style={{
              flexShrink: 0,
              padding: '0 16px',
              backgroundColor: colorBgContainer,
              borderBottom: '1px solid rgba(0,0,0,0.06)',
            }}
          >
            <Tabs
              activeKey={activeTab}
              type="editable-card"
              hideAdd
              onEdit={(key) => onRemoveTab(key as string)}
              onTabClick={onTabClick}
              tabBarStyle={{
                marginBottom: 0,
              }}
              items={tabs.map((tab) => ({
                key: tab.key,
                label: tab.label,
                closable: tab.closable,
              }))}
              tabBarExtraContent={{
                left: (
                  <Button
                    type="text"
                    icon={isCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                    onClick={() => {
                      localStorage.setItem('collapsed', isCollapsed ? 'expand' : 'collapsed');

                      setisCollapsed(!isCollapsed);
                    }}
                    style={{ marginRight: 12 }}
                  />
                ),
                right: (
                  <>
                    <Button type="link" icon={isDark ? <SunOutlined /> : <MoonOutlined />} onClick={changeTheme} />

                    <Popover
                      placement="bottomRight"
                      arrow
                      content={
                        <Space direction="vertical" size="small" style={{ width: 230 }}>
                          <Flex vertical>
                            <Typography.Text>
                              {fetchProfile.data?.firstName} {fetchProfile.data?.lastName}
                            </Typography.Text>

                            <Typography.Text type="secondary">{fetchProfile.data?.email}</Typography.Text>
                          </Flex>

                          <Flex justify="end">
                            <Typography.Text strong>{fetchProfile.data?.role}</Typography.Text>
                          </Flex>

                          <Divider style={{ margin: '8px 0' }} />

                          <Flex justify="center">
                            <Button type="link" icon={<LogoutOutlined />} onClick={onCloseSession}>
                              Cerrar sesión
                            </Button>
                          </Flex>
                        </Space>
                      }
                    >
                      <Button type="link" icon={<UserOutlined />} />
                    </Popover>
                  </>
                ),
              }}
            />
          </div>

          {/* CONTENIDO CON SCROLL */}
          <div
            className="flex flex-col"
            style={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: 16,
              backgroundColor: colorBgContainer,
            }}
          >
            {tabs.map((tab) => (
              <div
                key={tab.key}
                className="flex-1 min-h-0"
                style={{
                  display: tab.key === activeTab ? 'block' : 'none',
                }}
              >
                {tab.children}
              </div>
            ))}
          </div>
        </Layout>
      </Layout>
    </>
  );
};

export default LayoutTemplate;
